import {createRow, type RowModel, type Table} from '@tanstack/react-table'

import type {MemexItemModel} from '../../../models/memex-item-model'
import type {SubIssuesByParentId} from '../../../state-providers/memex-items/queries/use-paginated-memex-items-query'
import {getRowId} from '../helpers/get-row-id'
import type {Row} from '../helpers/types'

/**
 * Custom getSubRows function that reads pre-grouped sub-issue items data from the query cache
 * and recursively creates sub-issue subrows for the given row.
 *
 * @param parentRow The parent row for which to get sub-issue sub-rows
 * @param subIssuesByParentId A mapping of parent issue IDs to their corresponding sub-issue items
 * @param table The TanStack Table instance
 * @param rowModel The TanStack Table row model
 *
 * @returns An array of sub-rows representing the sub-issues of the given parent row
 */

export const getSubIssueSubRows = (
  parentRow: Row<MemexItemModel>,
  subIssuesByParentId: SubIssuesByParentId,
  table: Table<MemexItemModel>,
  rowModel: RowModel<MemexItemModel>,
) => {
  // Get sub-issue items available from the query cache by the row issue's id.
  const data = subIssuesByParentId[parentRow.original.content.id]
  // If not loaded, return empty array.
  if (!data) return []

  // Initialize custom subIssueData row model property if not set.
  if (!parentRow.subIssueData) {
    parentRow.subIssueData = {subIssueDepth: 0}
  }

  const {subIssues: items, missingSubIssues} = data
  // Sub-issue depth is separate from row depth to account for grouping.
  // Parent subIssueDepth should be defined at this point since it is set above, this is just to appease typechecking.
  const subIssueDepth = (parentRow.subIssueData?.subIssueDepth ?? -1) + 1
  const subIssues = items.map((item, index) => {
    // Create a row id that is unique within the context of the row's ancestors.
    // Built recursively in the format of `${parentRowId}-${itemId}`.
    const newRowId = getRowId(item, index, {id: parentRow.id})
    const newRow = createRow<MemexItemModel>(table, newRowId, item, index, parentRow.depth + 1, undefined, parentRow.id)
    newRow.subIssueData = {subIssueDepth}

    rowModel.flatRows.push(newRow)
    // Recursively get sub-rows for the new row.
    newRow.subRows = getSubIssueSubRows(newRow, subIssuesByParentId, table, rowModel)
    rowModel.rowsById[newRow.id] = newRow

    return newRow
  })

  // If the parent issue has missing sub-issues, add a custom row to represent them.
  // Sub-issues are considered missing if they are not in the project.
  if (missingSubIssues && missingSubIssues.length > 0) {
    const missingRow = createRow<MemexItemModel>(
      table,
      getRowId({id: 'missing'}, subIssues.length, {id: parentRow.id}),
      parentRow.original,
      subIssues.length,
      parentRow.depth + 1,
      undefined,
      parentRow.id,
    )
    missingRow.subIssueData = {
      subIssueDepth,
      missingSubIssues,
    }
    rowModel.flatRows.push(missingRow)
    rowModel.rowsById[missingRow.id] = missingRow
    subIssues.push(missingRow)
  }

  return subIssues
}
